import { defineStore } from 'pinia';
import { cloneDeep, get, each, find, map } from 'lodash';
import Vue from 'vue';

import CartApi from '@/extensions/shop/store/api/cart';
import pinia from '@/pinia';
import { useSetupStore } from '@/stores';

// Multistore: the store the admin UI currently has selected. Lists of store-owned
// records default to it, so a satellite stops showing its siblings' rows; the
// store filter widens them again.
const selectedStoreId = () => _.get(useSetupStore(pinia), 'activeCms.id', null);

const defaultForm = {
    id: undefined
};

const defaultRules = {};

const defaultUi = {
    form: {
        initialized: false,
        validateOnEnter: false
    },
    articlesPaginated: {
        loading: true
    }
};

export const useShopCartStore = defineStore('shopCart', {
    state: () => ({
        loading: true,
        cartsPaginated: {
            path: null,
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            link: {},
            items: []
        },
        articlesPaginated: {
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0,
                selection: {
                    type: 'inclusive',
                    identifiers: [],
                    total: 0,
                },
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {},
        form: cloneDeep(defaultForm),
        rules: cloneDeep(defaultRules),
        ui: cloneDeep(defaultUi)
    }),

    getters: {
        formInitialized: (state) => get(state, 'ui.form.initialized'),
        formValidateOnEnter: (state) => get(state, 'ui.form.validateOnEnter')
    },

    actions: {
        addPaginatedCartsToState(data) {
            this.cartsPaginated.items = data.items;
            this.cartsPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.cartsPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.cartsPaginated.link = cloneDeep(data.config.link);
            this.cartsPaginated.showFilters = get(data.config, 'showFilters') || false;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.cartsPaginated.allColumns, column => {
                let allowed = find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.cartsPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.cartsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.cartsPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.cartsPaginated.filters, key);
            } else {
                Vue.set(this.cartsPaginated.filters, key, value);
            }
        },

        defineFilterVisibility(value) {
            Vue.set(this.cartsPaginated, 'showFilters', value);
        },

        defineSorting({ key, value }) {
            Vue.set(this.cartsPaginated.variables, 'order', key);
            Vue.set(this.cartsPaginated.variables, 'direction', value);
        },

        // Cart articles
        addPaginatedArticlesToState(data) {
            this.articlesPaginated.items = data.items;
            // Preserve selection when updating pageInfo
            const currentSelection = cloneDeep(this.articlesPaginated.pageInfo.selection);
            this.articlesPaginated.pageInfo = cloneDeep(data.pageInfo);
            Vue.set(this.articlesPaginated.pageInfo, 'selection', currentSelection || { type: 'inclusive', identifiers: [], total: 0 });
            this.articlesPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.articlesPaginated.link = cloneDeep(data.config.link);
            this.articlesPaginated.showFilters = get(data.config, 'showFilters') || false;
            this.articlesPaginated.defaultSort = get(data.config, 'defaultSort') || null;
        },

        defineArticlesActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.articlesPaginated.allColumns, column => {
                let allowed = find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.articlesPaginated.activeColumns = activeColumns;
            }
        },

        defineArticlesPerPage(perPage) {
            this.articlesPaginated.pageInfo.perPage = perPage;
        },

        defineArticlesCurrentPage(currentPage) {
            this.articlesPaginated.pageInfo.currentPage = currentPage;
        },

        defineArticlesFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.articlesPaginated.filters, key);
            } else {
                Vue.set(this.articlesPaginated.filters, key, value);
            }
        },

        defineArticlesFilterVisibility(value) {
            Vue.set(this.articlesPaginated, 'showFilters', value);
        },

        defineArticlesSorting({ key, value }) {
            Vue.set(this.articlesPaginated.variables, 'order', key);
            Vue.set(this.articlesPaginated.variables, 'direction', value);
        },

        defineArticlesPaginationSizes(value) {
            Vue.set(this.articlesPaginated.variables, 'paginationSizes', value);
        },

        updateArticlesPaginationLoading(value) {
            this.ui.articlesPaginated.loading = value;
        },

        updateLoading(value) {
            this.loading = value;
        },

        updateRules(rules) {
            Vue.set(this, 'rules', rules);
        },

        resetRules() {
            Vue.set(this, 'rules', cloneDeep(defaultRules));
        },

        resetUI() {
            Vue.set(this, 'ui', cloneDeep(defaultUi));
        },

        insertCart(cart) {
            Vue.set(this, 'form', cart);
        },

        setFormInitialized(flag) {
            Vue.set(this.ui.form, 'initialized', flag);
        },

        setFormValidateOnEnter(flag) {
            Vue.set(this.ui.form, 'validateOnEnter', flag);
        },

        setFormLatitude({ i, value }) {
            Vue.set(this.form.customer.member.addresses[i], 'latitude', value);
        },

        setFormLongitude({ i, value }) {
            Vue.set(this.form.customer.member.addresses[i], 'longitude', value);
        },

        // Async actions
        fetchCartsPaginated() {
            return CartApi.fetchCartsPaginated({ cms: selectedStoreId(), ...this.cartsPaginated }).then(data => {
                this.addPaginatedCartsToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchArticlesPaginated(payload = {}) {
            return CartApi.fetchCartArticlesPaginated({ ...payload, ...this.articlesPaginated }, this.form.id).then(data => {
                this.addPaginatedArticlesToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineArticlesActiveColumns(activeColumnKeys);
            });
        },

        fetchCart(id) {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;

            this.articlesPaginationBulkSelectionReset();

            return CartApi.fetchCart(id, cms.id).then(cart => {
                this.insertCart(cloneDeep(cart));
            });
        },

        reset() {
            this.insertCart(cloneDeep(defaultForm));
            this.articlesPaginationBulkSelectionReset();
        },

        // Bulk selection actions for articles
        articlesPaginationBulkSelectionCheckAll({ value }) {
            let selection = this.articlesPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'exclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', this.articlesPaginated.pageInfo.total);
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', selection.identifiers.length);
            }
        },

        articlesPaginationBulkSelectionCheckVisible({ value }) {
            const items = get(this.articlesPaginated, 'items') || [];
            const ids = items.map(el => parseInt(el.id));

            let selection = this.articlesPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'inclusive');

                if (!selection.identifiers.length) {
                    Vue.set(selection, 'identifiers', ids);
                } else {
                    Vue.set(selection, 'identifiers', [...selection.identifiers, ...ids]);
                }
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', selection.identifiers.filter(el => ids.includes(el) === false));
            }

            Vue.set(selection, 'identifiers', [...new Set(selection.identifiers)]);
            Vue.set(selection, 'total', selection.identifiers.length);
        },

        articlesPaginationBulkSelectionCheckItem(item) {
            let selection = this.articlesPaginated.pageInfo.selection;
            const id = parseInt(item.id);

            let checked = false;
            switch (selection.type) {
                case 'inclusive':
                    checked = !!~selection.identifiers.map(el => parseInt(el)).indexOf(id);
                    break;
                case 'exclusive':
                    checked = !~selection.identifiers.map(el => parseInt(el)).indexOf(id);
                    break;
            }

            let push = true;
            let incr = true;
            switch (selection.type) {
                case 'inclusive':
                    push = incr = checked === false;
                    break;
                case 'exclusive':
                    push = checked === true;
                    incr = checked === false;
                    break;
            }

            if (push === true) {
                const index = selection.identifiers.length;
                Vue.set(selection.identifiers, index, id);
            } else {
                const index = selection.identifiers.indexOf(id);
                if (index >= 0) Vue.delete(selection.identifiers, index);
            }

            if (incr === true) Vue.set(selection, 'total', selection.total + 1);
            else Vue.set(selection, 'total', selection.total - 1);
        },

        articlesPaginationBulkSelectionReset() {
            Vue.set(this.articlesPaginated.pageInfo, 'selection', {
                type: 'inclusive',
                identifiers: [],
                total: 0,
            });
        },

        processArticlesPaginationBulkAction(action, cartId) {
            return CartApi.processCartArticlePaginationBulkAction(action, this.articlesPaginated, cartId);
        },
    }
});
